// FILE: src/hooks/usePaceInterventions.js
//
// Thin React wrapper around the pure pace-intervention reducer
// (services/paceInterventions). Holds the reducer state in a ref, evaluates on
// each pace update, and manages the single active message's auto-dismiss timer.
// All decision logic lives in the reducer; this hook only does plumbing.
//
// Returns { message, dismiss }. message is { type, text } | null.

import { useState, useRef, useEffect, useCallback } from 'react';
import { createInterventionState, nextPaceMessage } from '../services/paceInterventions';
import { trackEvent } from '../services/telemetryService';

export default function usePaceInterventions({
  enabled,
  snapshot,
  answeredCount,
  currentQuestionSeconds,
  currentQuestionKey,
  milestones,
  config,
  examId,
  mode,
}) {
  const [message, setMessage] = useState(null);
  const stateRef = useRef(createInterventionState());
  const dismissTimerRef = useRef(null);

  const dismiss = useCallback(() => {
    clearTimeout(dismissTimerRef.current);
    setMessage(null);
  }, []);

  useEffect(() => {
    // Don't evaluate (or carry stale state) when off, invalid, or still warming
    // up — the live card is neutral during warmup, so coaching stays quiet too.
    if (!enabled || !snapshot || !snapshot.valid || snapshot.warmup) return;

    const { message: msg, state } = nextPaceMessage(
      stateRef.current,
      {
        now: Date.now(),
        status: snapshot.status,
        deltaSeconds: snapshot.deltaSeconds,
        answeredCount,
        currentQuestionSeconds: currentQuestionSeconds ?? 0,
        currentQuestionKey,
        milestones,
      },
      config || {},
    );
    stateRef.current = state;

    if (msg) {
      setMessage(msg);
      clearTimeout(dismissTimerRef.current);
      const ms = ((config && config.messageDurationSeconds) || 8) * 1000;
      dismissTimerRef.current = setTimeout(() => setMessage(null), ms);
      // Telemetry (anonymized): which coaching message fired, and the pace band.
      trackEvent('pace_intervention_shown', 'usage', {
        feature: 'pace_coaching',
        exam_id: examId,
        mode,
        status: msg.type,
        pace_band: snapshot.status,
      });
    }
  }, [enabled, snapshot, answeredCount, currentQuestionSeconds, currentQuestionKey, milestones, config, examId, mode]);

  // Clean up the timer on unmount.
  useEffect(() => () => clearTimeout(dismissTimerRef.current), []);

  return { message, dismiss };
}
